import type { EditorSDK } from '@wix/platform-editor-sdk';

import type { EditorReadyOptions } from '../../../../types';
import { getMyAccountInstallDefinition, SOCIAL_APPS_IDS } from '../../../constants';
import { isDependencyInstallation, isStandaloneInstallation } from '../../../util';
import { installApps } from '../integration';
import { signalBlocksPlatformReady } from '../services/blocks-platform-ready';
import { uninstallMembersArea } from '../services/uninstall';
import {
  monitoredAddInitialMenuItemsToMemberMenus,
  monitoredAddLoginMenus,
  monitoredAddMemberSettingsSubMenu,
  monitoredAddProfileSubMenu,
  monitoredInstallMembersBlocksPlatform,
  monitoredNavigateToMemberSettingsPage,
} from './monitored-methods';

const isSocialInstallation = (options: EditorReadyOptions) => {
  const appTriggeredInstall = options.origin.info?.appDefinitionId;
  return appTriggeredInstall ? SOCIAL_APPS_IDS.includes(appTriggeredInstall) : false;
};

export const installMembersArea = async (editorSDK: EditorSDK, options: EditorReadyOptions) => {
  try {
    await Promise.all([
      monitoredAddMemberSettingsSubMenu(editorSDK),
      monitoredAddProfileSubMenu(editorSDK),
    ]);

    // BOB's editorReady calls connectToInnerElement for the sub-menus above,
    // so it must start only after they exist in DM.
    await Promise.all([
      monitoredInstallMembersBlocksPlatform(editorSDK, isSocialInstallation(options)),
      monitoredAddLoginMenus(editorSDK, options),
    ]);

    // Signal before MyAccount install to prevent deadlock: MyAccount calls
    // the public API which awaits this signal via waitForBlocksPlatformReady()
    signalBlocksPlatformReady();

    const isMigrating = options.origin.info?.migrationData?.isMigrating;
    const isDependency = await isDependencyInstallation(options);
    const installMyAccount = isStandaloneInstallation(options) || isDependency || isMigrating;

    if (installMyAccount) {
      await installApps({ editorSDK, definitions: [getMyAccountInstallDefinition(options.origin)] });
    }

    await monitoredAddInitialMenuItemsToMemberMenus(editorSDK, options);

    if (isStandaloneInstallation(options)) {
      monitoredNavigateToMemberSettingsPage(editorSDK);
    }
  } catch (e) {
    try {
      await uninstallMembersArea(editorSDK);
    } catch (err) {}

    throw e;
  }
};
